import { useState, useEffect, lazy, Suspense } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Header from "./Header";
import CategoryBar from "./CategoryBar";
import Footer from "./Footer";
import ToastContainer from "../ui/Toast";
import EmailVerificationBanner from "./EmailVerificationBanner";
import GlobalBanner from "./GlobalBanner";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

// AuthModal may not exist yet — loaded lazily to avoid hard import errors
// TODO: create components/auth/AuthModal.jsx when auth flow is implemented
let AuthModal = null;
try {
  AuthModal = lazy(() => import("../auth/AuthModal"));
} catch {
  AuthModal = null;
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const { user, refreshUser } = useAuth();

  const showCategories = ["/", "/buscar"].includes(location.pathname);

  const [activeCategory, setActiveCategory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [authModal, setAuthModal] = useState(null); // null | "login" | "register"

  // Resetear filtro de categoría ao volver á home (clic no logo Leyro).
  // /buscar conserva o filtro porque o URL leva ?type=...&minPrice=... etc.
  // (UX estándar: shareable / bookmarkable). En "/" si limpamos.
  useEffect(() => {
    if (location.pathname === "/") setActiveCategory("all");
  }, [location.pathname]);

  // Backend redirixe a "/?emailVerified=1" tras confirmar o email.
  // Refrescamos o user para que o banner de verificación desapareza sen F5.
  useEffect(() => {
    if (searchParams.get("emailVerified") === "1") {
      addToast("Email confirmado. Xa tes a conta activada ✓", "success");
      const next = new URLSearchParams(searchParams);
      next.delete("emailVerified");
      setSearchParams(next, { replace: true });
      if (user) refreshUser().catch(() => {});
    }
  }, [searchParams, setSearchParams, addToast, user, refreshUser]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header onOpenAuth={(mode) => setAuthModal(mode)} />

      <GlobalBanner />
      <EmailVerificationBanner />

      {showCategories && (
        <CategoryBar
          activeCategory={activeCategory}
          onCategoryChange={(catId) => {
            // Caso especial: BANCO_TERRAS é un atallo a /buscar con filtro
            // source, NON unha categoría de produto. Aplica desde calquera
            // ruta.
            if (catId === "BANCO_TERRAS") {
              navigate("/buscar?type=finca&source=BANCO_TERRAS");
              return;
            }
            // En home, clic nunha categoría leva a /buscar co filtro xa
            // aplicado. En /buscar, só actualiza o estado local.
            if (location.pathname === "/" && catId !== "all") {
              navigate(`/buscar?category=${catId}`);
              return;
            }
            setActiveCategory(catId);
          }}
          onOpenFilters={() => {
            // En home non hai FiltersModal renderizado — redirixe a /buscar
            // co modal aberto via ?openFilters=1.
            if (location.pathname === "/") {
              navigate("/buscar?openFilters=1");
              return;
            }
            setShowFilters(true);
          }}
        />
      )}

      <main className="flex-1">
        <Outlet
          context={{
            activeCategory,
            showFilters,
            setShowFilters,
            authModal,
            setAuthModal,
          }}
        />
      </main>

      <Footer />

      <ToastContainer />

      {/* Auth modal — rendered when authModal is "login" or "register" */}
      {AuthModal !== null && (
        <Suspense fallback={null}>
          <AuthModal
            open={!!authModal}
            initialMode={authModal}
            onClose={() => setAuthModal(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
